import { MobileNav } from "@/components/layout/MobileNav";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState, type FocusEvent, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Link, useLocation } from "react-router-dom";
import { SERVICES, resolveLocale } from "@/data/serviceDetails";

export const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboutMenuOpen, setIsAboutMenuOpen] = useState(false);
  const [isProjectsMenuOpen, setIsProjectsMenuOpen] = useState(false);

  const navLinks = [
    { href: "/#services", label: t("nav.services") },
    { href: "/#partners", label: t("nav.partners") },
    { href: "/#process", label: t("nav.process") },
    { href: "/#regions", label: t("nav.regions") },
    { href: "/blog", label: t("nav.blog", "Blog") },
    { href: "/#contact", label: t("nav.contact") },
  ];

  const aboutMenuItems = [
    { key: "who_we_are", label: t("nav.about_menu.who_we_are"), href: "/kurumsal#who-we-are" },
    { key: "vision", label: t("nav.about_menu.vision"), href: "/kurumsal#vision" },
    { key: "mission", label: t("nav.about_menu.mission"), href: "/kurumsal#mission" },
    { key: "values", label: t("nav.about_menu.values"), href: "/kurumsal#values" },
  ];
  const isLightBackgroundRoute =
    location.pathname.startsWith("/kurumsal") ||
    location.pathname.startsWith("/hakkimizda") ||
    location.pathname.startsWith("/projeler");

  const navLinkClass = cn(
    "font-medium transition-colors",
    isScrolled || isLightBackgroundRoute
      ? "text-neutral-900 hover:text-primary-500"
      : "text-white hover:text-primary-50"
  );

  const locale = resolveLocale(i18n.language || "en");

  const projectMenuItems = useMemo(() =>
    SERVICES.map((service) => ({
      id: service.id,
      href: `/projeler/${service.slug}`,
      label: service.translations[locale]?.name ?? service.translations.en.name,
    })),
  [locale]);

  const handleAboutBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocused = event.relatedTarget as Node | null;
    if (!nextFocused || !event.currentTarget.contains(nextFocused)) {
      setIsAboutMenuOpen(false);
    }
  };

  const handleAboutMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    const nextHovered = event.relatedTarget as Node | null;
    if (!nextHovered || !event.currentTarget.contains(nextHovered)) {
      setIsAboutMenuOpen(false);
    }
  };

  const handleProjectsBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocused = event.relatedTarget as Node | null;
    if (!nextFocused || !event.currentTarget.contains(nextFocused)) {
      setIsProjectsMenuOpen(false);
    }
  };

  const handleProjectsMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    const nextHovered = event.relatedTarget as Node | null;
    if (!nextHovered || !event.currentTarget.contains(nextHovered)) {
      setIsProjectsMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 w-full z-50 transition-all duration-300",
        isScrolled || isLightBackgroundRoute ? "bg-white/90 shadow-md backdrop-blur-sm" : "bg-transparent"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div>
            <Link to="/" aria-label={t('nav.home', 'Go to home')}>
              <img src="/images/logo/INS_1_192.png" srcSet="/images/logo/INS_1_192.png 192w, /images/logo/INS_1_512.png 512w" sizes="(min-width: 768px) 160px, 160px" alt="INS Organization" className="header-logo" width={192} height={192} decoding="async" loading="eager" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <div
              className="relative"
              onMouseEnter={() => setIsAboutMenuOpen(true)}
              onMouseLeave={handleAboutMouseLeave}
              onFocus={() => setIsAboutMenuOpen(true)}
              onBlur={handleAboutBlur}
            >
              <Link
                to="/kurumsal"
                aria-haspopup="true"
                aria-expanded={isAboutMenuOpen}
                className={navLinkClass}
                onClick={() => setIsAboutMenuOpen(false)}
              >
                {t("nav.about_menu.label")}
              </Link>
              <div
                className={cn(
                  "absolute left-1/2 top-full z-40 mt-0 w-80 -translate-x-1/2 rounded-2xl border border-neutral-200 bg-white/95 pt-4 pb-4 px-4 shadow-xl backdrop-blur-sm transition-all duration-200",
                  isAboutMenuOpen
                    ? "visible opacity-100 pointer-events-auto"
                    : "invisible opacity-0 pointer-events-none"
                )}
                onMouseEnter={() => setIsAboutMenuOpen(true)}
              >
                <div className="flex flex-col overflow-hidden rounded-xl border border-neutral-200/70 divide-y divide-neutral-200/70">
                  {aboutMenuItems.map((item) => (
                    <Link
                      key={item.key}
                      to={item.href}
                      className="px-4 py-3 text-sm font-medium text-neutral-500 transition-all hover:bg-brand-turquoise/10 hover:text-brand-turquoise focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-turquoise/40"
                      onClick={() => setIsAboutMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <div
              className="relative"
              onMouseEnter={() => setIsProjectsMenuOpen(true)}
              onMouseLeave={handleProjectsMouseLeave}
              onFocus={() => setIsProjectsMenuOpen(true)}
              onBlur={handleProjectsBlur}
            >
              <Link
                to={projectMenuItems[0]?.href || "/projeler"}
                aria-haspopup="true"
                aria-expanded={isProjectsMenuOpen}
                className={navLinkClass}
                onClick={() => setIsProjectsMenuOpen(false)}
              >
                {t("nav.projects")}
              </Link>
              <div
                className={cn(
                  "absolute left-1/2 top-full z-40 mt-0 w-80 -translate-x-1/2 rounded-2xl border border-neutral-200 bg-white/95 pt-4 pb-4 px-4 shadow-xl backdrop-blur-sm transition-all duration-200",
                  isProjectsMenuOpen
                    ? "visible opacity-100 pointer-events-auto"
                    : "invisible opacity-0 pointer-events-none"
                )}
                onMouseEnter={() => setIsProjectsMenuOpen(true)}
              >
                <div className="flex max-h-96 flex-col overflow-y-auto rounded-xl border border-neutral-200/70 divide-y divide-neutral-200/70">
                  {projectMenuItems.map((item) => (
                    <Link
                      key={item.id}
                      to={item.href}
                      className="px-4 py-3 text-sm font-medium text-neutral-500 transition-all hover:bg-brand-turquoise/10 hover:text-brand-turquoise focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-turquoise/40"
                      onClick={() => setIsProjectsMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={navLinkClass}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          
          {/* Sign In, Language, and Mobile Menu Trigger */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <div className="md:hidden">
              <MobileNav />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
